// Impuesto a la renta en relación de dependencia (LORTI): proyección anual de
// gastos personales (rebaja aplicada por el motor del rol) y Formulario 107.
// Nómina → IESS y Tributario.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { AnexoDescarga } from './AnexoDescarga';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Modal, fieldCls, money, nomRpc, type Form107Row, type IrProyeccionRow } from './shared';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';

const ANIO = new Date().getFullYear();

function Proyecciones({ anio }: { anio: number }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const { data: rows = [], isFetching } = useQuery<IrProyeccionRow[]>({
    queryKey: ['nom-ir-proyecciones', anio],
    queryFn: () => nomRpc<IrProyeccionRow[]>('nom_ir_proyecciones', { _anio: anio }),
  });
  const filas = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? rows.filter((r) => r.razon_social.toLowerCase().includes(t) || r.identificacion.includes(t)) : rows;
  }, [rows, q]);

  const [editando, setEditando] = useState<IrProyeccionRow | null>(null);
  const [gastos, setGastos] = useState('');
  const [rebajaManual, setRebajaManual] = useState('');

  const headersExp = ['N°', 'Código', 'Empleado', 'Gastos proyectados', 'Cargas IR', 'Límite (canastas)', 'Rebaja aplicada (anual)'];
  const MONEYCOLS = [3, 5, 6];
  const filasExport: Celda[][] = [
    ...filas.map((r, i): Celda[] => [
      i + 1, r.empleado_codigo, r.razon_social,
      r.gastos_personales != null ? Number(r.gastos_personales) : '', Number(r.cargas_ir),
      r.limite != null ? Number(r.limite) : '', Number(r.rebaja_aplicada),
    ]),
    ['', '', 'TOTAL',
      filas.reduce((s, r) => s + Number(r.gastos_personales ?? 0), 0),
      filas.reduce((s, r) => s + Number(r.cargas_ir), 0),
      filas.reduce((s, r) => s + Number(r.limite ?? 0), 0),
      filas.reduce((s, r) => s + Number(r.rebaja_aplicada), 0)],
  ];

  const guardar = useMutation({
    mutationFn: () => nomRpc('nom_ir_proyeccion_guardar', {
      _data: { empleado_id: editando!.empleado_id, anio, gastos_personales: Number(gastos) || 0, rebaja_manual: rebajaManual === '' ? null : Number(rebajaManual) },
    }),
    onSuccess: () => { toast.success('Proyección guardada'); setEditando(null); void qc.invalidateQueries({ queryKey: ['nom-ir-proyecciones'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <input className={`${fieldCls} w-72`} placeholder="Filtrar por nombre o cédula…" value={q} onChange={(e) => setQ(e.target.value)} />
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
      </div>
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Gastos personales proyectados {anio} ({filas.length})</CardTitle>
          <ExportarBotones
            nombre={`ir-proyeccion-gastos-${fechaLocal()}`} titulo="Impuesto a la Renta — Proyección de gastos"
            headers={headersExp} filas={filasExport} colsDerecha={[0, 3, 4, 5, 6]}
            fmtPDF={(v, c) => (MONEYCOLS.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
            disabled={filas.length === 0} />
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Empleado</th>
              <th className="py-2 pr-3 text-right">Gastos proyectados</th><th className="py-2 pr-3 text-right">Cargas IR</th>
              <th className="py-2 pr-3 text-right">Límite (canastas)</th><th className="py-2 pr-3 text-right">Rebaja aplicada (anual)</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filas.map((r) => (
                <tr key={r.empleado_id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{r.empleado_codigo}</td>
                  <td className="py-2 pr-3">{r.razon_social}<div className="text-xs text-muted-foreground font-mono">{r.identificacion}</div></td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{r.gastos_personales != null ? money(r.gastos_personales) : '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{r.cargas_ir}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-muted-foreground">{r.limite != null ? money(r.limite) : 'sin canasta'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">
                    {money(r.rebaja_aplicada)}{r.rebaja_manual != null && <span className="text-xs text-muted-foreground"> (manual)</span>}
                  </td>
                  <td className="py-2 text-right">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar proyección"
                      onClick={() => { setEditando(r); setGastos(r.gastos_personales != null ? String(r.gastos_personales) : ''); setRebajaManual(r.rebaja_manual != null ? String(r.rebaja_manual) : ''); }}>
                      <Pencil className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {editando && (
        <Modal title={`Proyección ${anio} — ${editando.razon_social}`} onClose={() => setEditando(null)}>
          <div className="space-y-3">
            <Campo label="Gastos personales proyectados del año ($)">
              <input type="number" min="0" step="0.01" className={fieldCls} value={gastos} onChange={(e) => setGastos(e.target.value)} />
            </Campo>
            <div className="text-xs text-muted-foreground">
              Rebaja automática = 18% del MENOR entre los gastos y el límite por canastas básicas según sus cargas IR
              (0→7 · 1→9 · 2→11 · 3→14 · 4→17 · 5+→20 canastas; canasta en Configuración → Parámetros).
              Límite del empleado: <span className="font-mono">{editando.limite != null ? money(editando.limite) : 'sin canasta configurada'}</span>.
            </div>
            <Campo label="Rebaja manual (opcional, prevalece sobre el cálculo)">
              <input type="number" min="0" step="0.01" className={fieldCls} value={rebajaManual} onChange={(e) => setRebajaManual(e.target.value)} placeholder="Vacío = usar el cálculo automático" />
            </Campo>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditando(null)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Formulario107({ anio }: { anio: number }) {
  const [q, setQ] = useState('');
  const { data: rows = [], isFetching } = useQuery<Form107Row[]>({
    queryKey: ['nom-form107', anio],
    queryFn: () => nomRpc<Form107Row[]>('nom_form107', { _anio: anio }),
  });
  const filas = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? rows.filter((r) => r.razon_social.toLowerCase().includes(t) || r.identificacion.includes(t)) : rows;
  }, [rows, q]);
  const tot = (k: keyof Form107Row) => filas.reduce((s, f) => s + Number(f[k] ?? 0), 0);

  const headersExp = ['N°', 'Código', 'Empleado', 'Ingresos gravados', 'Ingresos exentos', 'Aporte IESS', 'Rebaja gastos', 'IR retenido'];
  const MONEYCOLS = [3, 4, 5, 6, 7];
  const filasExport: Celda[][] = [
    ...filas.map((f, i): Celda[] => [
      i + 1, f.empleado_codigo, f.razon_social,
      Number(f.ingresos_gravados), Number(f.ingresos_exentos), Number(f.aporte_iess), Number(f.rebaja_gastos), Number(f.ir_retenido),
    ]),
    ['', '', 'TOTAL', tot('ingresos_gravados'), tot('ingresos_exentos'), tot('aporte_iess'), tot('rebaja_gastos'), tot('ir_retenido')],
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <input className={`${fieldCls} w-72`} placeholder="Filtrar por nombre o cédula…" value={q} onChange={(e) => setQ(e.target.value)} />
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
      </div>
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Formulario 107 — retenciones en relación de dependencia {anio} ({filas.length})</CardTitle>
          <ExportarBotones
            nombre={`formulario-107-${fechaLocal()}`} titulo="Impuesto a la Renta — Formulario 107"
            headers={headersExp} filas={filasExport} colsDerecha={[0, ...MONEYCOLS]}
            fmtPDF={(v, c) => (MONEYCOLS.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
            disabled={filas.length === 0} />
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Empleado</th>
              <th className="py-2 pr-3 text-right">Ingresos gravados</th><th className="py-2 pr-3 text-right">Ingresos exentos</th>
              <th className="py-2 pr-3 text-right">Aporte IESS</th><th className="py-2 pr-3 text-right">Rebaja gastos</th>
              <th className="py-2 pr-3 text-right">IR retenido</th>
            </tr></thead>
            <tbody>
              {filas.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin roles cerrados en {anio}</td></tr>}
              {filas.map((f, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{f.empleado_codigo}</td>
                  <td className="py-2 pr-3">{f.razon_social}<div className="text-xs text-muted-foreground font-mono">{f.identificacion}</div></td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.ingresos_gravados)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums text-muted-foreground">{money(f.ingresos_exentos)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.aporte_iess)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(f.rebaja_gastos)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(f.ir_retenido)}</td>
                </tr>
              ))}
              {filas.length > 0 && (
                <tr className="font-semibold bg-muted/30">
                  <td className="py-2 pr-3" colSpan={2}>TOTALES</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('ingresos_gravados'))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('ingresos_exentos'))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('aporte_iess'))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('rebaja_gastos'))}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tot('ir_retenido'))}</td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

export function ImpuestoRenta() {
  const [anio, setAnio] = useState(ANIO);
  const [tab, setTab] = useState<'proyeccion' | 'form107' | 'rdep'>('proyeccion');
  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Impuesto a la Renta</h1>
        <input type="number" className={`${fieldCls} w-24`} value={anio} onChange={(e) => setAnio(Number(e.target.value))} aria-label="Año" />
      </div>
      <div className="flex gap-1 border-b">
        {([['proyeccion', 'Proyección de gastos'], ['form107', 'Formulario 107'], ['rdep', 'Anexo RDEP']] as const).map(([k, v]) => (
          <button key={k} className={`px-4 py-2 text-sm rounded-t-md ${tab === k ? 'bg-card border border-b-0 font-medium' : 'text-muted-foreground hover:bg-muted'}`}
            onClick={() => setTab(k)} aria-pressed={tab === k}>{v}</button>
        ))}
      </div>
      {tab === 'proyeccion' && <Proyecciones anio={anio} />}
      {tab === 'form107' && <Formulario107 anio={anio} />}
      {tab === 'rdep' && (
        <AnexoDescarga tipo="rdep" anio={anio}
          titulo={`Anexo de Relación de Dependencia ${anio} — archivo para el DIMM`} />
      )}
    </div>
  );
}
